import { ThemedText } from "@/components/themed-text";
import { BrandColors, Colors } from "@/constants/theme";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { Vehicle } from "@/lib/services/vehicles";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import {
  formatMileage,
  formatVehicleLabel,
  getAvailabilityBadge,
  getVehiclePhotoUrl,
  getVehicleTitle,
  getVehicleTypeShortLabel,
} from "./vehicle-card-utils";

interface VehicleCardProps {
  vehicle: Vehicle;
  backgroundColor?: string;
  borderColor?: string;
  onPress?: () => void;
  desktop?: boolean;
}

export const VehicleCard = React.memo(function VehicleCard({
  vehicle,
  backgroundColor = BrandColors.white,
  borderColor = BrandColors.lightGray,
  onPress,
  desktop = false,
}: VehicleCardProps) {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const theme = Colors[colorScheme ?? "light"];
  const photoUrl = getVehiclePhotoUrl(vehicle);
  const statusBadge = getAvailabilityBadge(vehicle.is_available);
  const mileage = formatMileage(vehicle.mileage);
  const vehicleTypeLabel = getVehicleTypeShortLabel(vehicle.vehicle_type);
  const title = getVehicleTitle(vehicle);
  const accessibilityLabel = `${title}. ${vehicle.registration_number ?? "No registration"}. ${statusBadge.label}`;

  const detailPills = [
    vehicle.make && {
      icon: "car-outline" as const,
      label: formatVehicleLabel(vehicle.make),
    },
    vehicleTypeLabel && {
      icon: "speedometer-outline" as const,
      label: vehicleTypeLabel,
    },
    vehicle.is_verified
      ? {
          icon: "checkmark-circle-outline" as const,
          label: "Verified",
        }
      : {
          icon: "time-outline" as const,
          label: "Pending",
        },
  ].filter(Boolean) as {
    icon: keyof typeof Ionicons.glyphMap;
    label: string;
  }[];

  const content = (
    <>
      <View style={[styles.imageContainer, desktop && styles.desktopImageContainer]}>
        {photoUrl ? (
          <Image
            source={{ uri: photoUrl }}
            style={[styles.vehicleImage, desktop && styles.desktopVehicleImage]}
            contentFit="cover"
            transition={200}
            recyclingKey={vehicle.id.toString()}
          />
        ) : (
          <View
            style={[
              styles.vehicleImage,
              desktop && styles.desktopVehicleImage,
              styles.noImageContainer,
              isDark && { backgroundColor: "#1B2436" },
            ]}
          >
            <Ionicons
              name="car-sport-outline"
              size={48}
              color={BrandColors.mediumGray}
            />
          </View>
        )}
      </View>

      <View style={[styles.cardBody, desktop && styles.desktopCardBody]}>
        <View style={styles.titleRow}>
          <ThemedText
            style={[styles.vehicleTitle, desktop && styles.desktopVehicleTitle]}
            type="defaultSemiBold"
            numberOfLines={desktop ? 1 : 2}
          >
            {getVehicleTitle(vehicle)}
          </ThemedText>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: statusBadge.backgroundColor },
            ]}
          >
            <ThemedText
              style={[styles.statusText, { color: statusBadge.color }]}
              type="extraSmallSemiBold"
            >
              {statusBadge.label}
            </ThemedText>
          </View>
        </View>

        <View style={styles.plateBadge}>
          <ThemedText style={styles.plateText} type="extraSmallSemiBold">
            {vehicle.registration_number?.toUpperCase()}
          </ThemedText>
        </View>

        <View style={styles.metaRow}>
          {mileage ? (
            <View style={styles.metaItem}>
              <Ionicons
                name="speedometer-outline"
                size={12}
                color={BrandColors.mediumGray}
              />
              <ThemedText style={styles.metaText} type="extraSmall">
                {mileage}
              </ThemedText>
            </View>
          ) : null}
          {vehicle.year ? (
            <View style={styles.metaItem}>
              <Ionicons
                name="calendar-outline"
                size={12}
                color={BrandColors.mediumGray}
              />
              <ThemedText style={styles.metaText} type="extraSmall">
                {vehicle.year}
              </ThemedText>
            </View>
          ) : null}
          {vehicle.model ? (
            <View style={styles.metaItem}>
              <View style={styles.metaDot} />
              <ThemedText style={styles.metaText} type="extraSmall">
                {formatVehicleLabel(vehicle.model)}
              </ThemedText>
            </View>
          ) : null}
        </View>

        {detailPills.length > 0 ? (
          <View style={styles.pillRow}>
            {detailPills.map((pill) => (
              <View
                key={pill.label}
                style={[
                  styles.pill,
                  desktop && styles.desktopPill,
                  isDark && {
                    backgroundColor: "#243044",
                    borderColor: theme.border,
                  },
                ]}
              >
                {!desktop && (
                  <Ionicons
                    name={pill.icon}
                    size={12}
                    color={BrandColors.primary}
                  />
                )}
                <ThemedText
                  style={[styles.pillText, isDark && { color: theme.text }]}
                  type="extraSmall"
                >
                  {pill.label}
                </ThemedText>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </>
  );

  return (
    <View
      style={[
        styles.card,
        desktop && styles.desktopCard,
        { backgroundColor, borderColor },
      ]}
    >
      {onPress ? (
        <TouchableOpacity
          onPress={onPress}
          activeOpacity={0.9}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
        >
          {content}
        </TouchableOpacity>
      ) : (
        content
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
    borderWidth: 1,
    overflow: "hidden",
  },
  imageContainer: {
    position: "relative",
  },
  vehicleImage: {
    width: "100%",
    height: 180,
    backgroundColor: BrandColors.backgroundGray,
  },
  noImageContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  cardBody: {
    padding: 16,
    gap: 10,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  vehicleTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: "700",
    lineHeight: 22,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 20,
    flexShrink: 0,
  },
  statusText: {
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 0.4,
  },
  plateBadge: {
    alignSelf: "flex-start",
    backgroundColor: BrandColors.primary,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  plateText: {
    color: BrandColors.white,
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  metaDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.primary,
  },
  metaText: {
    color: BrandColors.mediumGray,
  },
  pillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: BrandColors.white,
  },
  pillText: {
    color: BrandColors.charcoal,
    fontWeight: "600",
  },
  desktopCard: {
    marginBottom: 0,
    borderRadius: 12,
  },
  desktopImageContainer: {
    padding: 10,
    paddingBottom: 0,
  },
  desktopVehicleImage: {
    height: 136,
    borderRadius: 8,
  },
  desktopCardBody: {
    paddingHorizontal: 10,
    paddingTop: 10,
    paddingBottom: 12,
    gap: 8,
  },
  desktopVehicleTitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  desktopPill: {
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
});
